import { z } from "zod";
import type { StateCreator } from "zustand/vanilla";

import {
  type CameraPlan,
  cameraPlanSchema,
  type RouteGeometry,
  routeGeometrySchema,
} from "../../studio/cameraPlan";
import {
  type ProfileSource,
  profileDesignSchema,
  profileSourceSchema,
} from "../../studio/profileSamples";
import { languageSchema, unitLengthSchema, unitSlopeSchema } from "../../studio/searchParams";

/**
 * videoExportStatusSchema is the video export wizard's progress state, a
 * discriminated union on `kind`: `idle` (the wizard is open but nothing is
 * running), `previewing` (a live camera flight plays, `fraction` 0..1 of the
 * way through), and `recording` (frames are being captured and encoded,
 * `fraction` 0..1 plus a human-readable `phase` such as the capture or encode
 * step). Zod-first because the value is shared with the status-set tool input
 * and validated when a preview or recording reports progress. The transient
 * non-serializable handles a recording needs (the abort controller, the
 * produced blob) live outside the store; only this serializable progress view
 * does.
 */
export const videoExportStatusSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("idle") }),
  z.object({ fraction: z.number().min(0).max(1), kind: z.literal("previewing") }),
  z.object({
    fraction: z.number().min(0).max(1),
    kind: z.literal("recording"),
    /**
     * phase mirrors the map package's `VideoRecordingPhase` values
     * (preparing → recording → encoding) — the recording run forwards its phase
     * through to this status. The literals are duplicated here on purpose: core
     * is a leaf and cannot import the map package, so a compatibility test pins
     * the two together rather than a shared import.
     */
    phase: z.enum(["preparing", "recording", "encoding"]),
  }),
]);

/**
 * VideoExportStatus is the video export wizard's progress state, derived from
 * videoExportStatusSchema.
 */
export type VideoExportStatus = z.infer<typeof videoExportStatusSchema>;

/**
 * videoExportProfileSchema is the live profile overlay's settings on the wizard:
 * whether the overlay is `enabled`, its `design` (the elevation graph or the
 * compact strip), and the video-scoped units + language it formats with. The
 * units/language are local to the export — they never touch the global preferences
 * or the map — so the overlay reads them straight into the param-based formatters.
 * Every field carries a default (a metric/degrees/English/graph seed) so a
 * present-but-empty object resolves; the open flow overrides the units + language
 * from the viewer's identity preferences. Zod-first; reuses the domain unit +
 * language schemas so the values stay the shared domain types, never free strings.
 */
export const videoExportProfileSchema = z.object({
  design: profileDesignSchema.default("graph"),
  enabled: z.boolean().default(false),
  language: languageSchema.default("en"),
  length: unitLengthSchema.default("metric"),
  slope: unitSlopeSchema.default("degrees"),
});

/**
 * VideoExportProfile is the live profile overlay's settings, derived from
 * videoExportProfileSchema.
 */
export type VideoExportProfile = z.infer<typeof videoExportProfileSchema>;

/**
 * DEFAULT_VIDEO_EXPORT_PROFILE is the seed the wizard opens the profile block with
 * before the open flow overrides the units + language from identity prefs: the
 * overlay off, the graph design, and the metric/degrees/English defaults. Held as a
 * const so videoExportProfileSchema's default factory and any seeding caller read
 * one literal.
 */
export const DEFAULT_VIDEO_EXPORT_PROFILE: VideoExportProfile = {
  design: "graph",
  enabled: false,
  language: "en",
  length: "metric",
  slope: "degrees",
};

/**
 * videoExportSchema is the video export wizard state: the itinerary it is open
 * for, an optional segment to narrow the flight to one segment (null = the whole
 * itinerary), the active wizard `step` (format / profile / camera / review), the
 * flattened `route` the camera plan is resolved against (null until the wizard's
 * itinerary detail loads and the route is computed), the shared `plan` the presets
 * and the camera edits author, the `status` progress view, the live `profile`
 * overlay settings (whether it is enabled, its design, and the video-scoped
 * units + language it formats with), and the plan-independent `profileSource` the
 * overlay resolves per-frame samples from. The wizard is opened from an itinerary
 * list row, which is not necessarily the studio's active itinerary, so the route
 * AND the profile source are sourced here (set by the route-set tool once the
 * detail loads) rather than read off the active itinerary — the whole feature is
 * decoupled from the editor's selection. The profile units/language default to a
 * sensible metric/degrees/English seed; the open flow overrides them from the
 * viewer's identity preferences. Zod-first because the value is shared with the
 * video-open tool input and validated when an agent or the UI opens the wizard; a
 * null slot (wizard closed) lives outside the schema as the absence of a value,
 * mirroring exportTargetSchema.
 */
export const videoExportSchema = z.object({
  // The resolved activity silhouette URL the moving focus dot masks, decoupled from the
  // studio's active itinerary like the route + source: the route-sync hook resolves it
  // from the exported itinerary's activity and stores it through the route-set tool. Null
  // when the itinerary carries no activity (a plain disc); defaulted + optional so a
  // wizard state built without it (the open path) still type-checks.
  activityGlyphUrl: z.string().nullable().default(null).optional(),
  itineraryId: z.string(),
  plan: cameraPlanSchema,
  // The profile block + its source are optional on the wire so the wizard can open
  // with just the plan (the open tool seeds neither yet); both default so a parse
  // fills them, and both are optional so a wizard state built without them (the
  // open path) still type-checks against VideoExport.
  profile: videoExportProfileSchema.default(() => DEFAULT_VIDEO_EXPORT_PROFILE).optional(),
  profileSource: profileSourceSchema.nullable().default(null).optional(),
  route: routeGeometrySchema.nullable().default(null),
  segmentId: z.string().nullable(),
  status: videoExportStatusSchema,
  step: z.enum(["format", "profile", "camera", "review"]),
});

/**
 * VideoExport is the video export wizard state, derived from videoExportSchema.
 * The video export slot holds this or null (the wizard is closed).
 */
export type VideoExport = z.infer<typeof videoExportSchema>;

/**
 * VideoExportSliceState is the open video export wizard slot.
 */
export interface VideoExportSliceState {
  /**
   * videoExport is the open video export wizard state (null = the wizard is
   * closed). It holds the itinerary + optional segment the flight is for, the
   * active wizard step, the shared Camera Plan the presets and camera edits
   * author, and the preview/recording progress. Set only through the tool bus,
   * like every other UiAction. The non-serializable recording handles (the
   * abort controller, the produced blob) live outside the store; this slice
   * carries only the serializable plan + progress.
   */
  videoExport: VideoExport | null;
}

/**
 * VideoExportSliceActions are the whole-slot and narrow-field setters that drive
 * the video export wizard, reached only through the tool bus.
 */
export interface VideoExportSliceActions {
  /**
   * setVideoExport opens the video export wizard with the given state, or
   * closes it when passed null. The whole-slot setter the open/close tools
   * drive; the narrower plan/status/step setters update one field of an
   * already-open wizard.
   */
  setVideoExport: (videoExport: VideoExport | null) => void;
  /**
   * setVideoExportActivityGlyph sets the resolved activity silhouette URL the moving
   * focus dot masks on the open wizard. The route-sync hook resolves it from the
   * exported itinerary's activity and stores it through the route-set tool alongside the
   * route, so the marker glyph does not depend on the studio's active itinerary. Null
   * clears it (a plain disc). A no-op when the wizard is closed, mirroring
   * setVideoExportProfileSource.
   */
  setVideoExportActivityGlyph: (url: string | null) => void;
  /**
   * setVideoExportPlan replaces the Camera Plan on the open wizard. A no-op
   * when the wizard is closed, so a defensive call from a preset generator or
   * an agent camera edit before the wizard opens is safe.
   */
  setVideoExportPlan: (plan: CameraPlan) => void;
  /**
   * setVideoExportProfile merges a partial profile-overlay update over the open
   * wizard's profile settings — a single field (the enable flag, the design, a
   * unit, the language) without restating the rest. The merge falls back to the
   * default seed when the wizard has no profile block yet, so a first update lands
   * a complete block. A no-op when the wizard is closed, mirroring the other narrow
   * video setters.
   */
  setVideoExportProfile: (profile: Partial<VideoExportProfile>) => void;
  /**
   * setVideoExportProfileSource sets the plan-independent profile source the
   * overlay resolves per-frame samples from on the open wizard. The route-sync
   * hook computes it from the exported itinerary's detail alongside the route, so
   * the overlay does not depend on the studio's active itinerary. A no-op when the
   * wizard is closed, mirroring setVideoExportRoute.
   */
  setVideoExportProfileSource: (source: ProfileSource | null) => void;
  /**
   * setVideoExportRoute sets the flattened route the Camera Plan resolves
   * against on the open wizard. The wizard fetches its exported itinerary's
   * detail by id and computes this route, so the feature does not depend on the
   * studio's active itinerary. A no-op when the wizard is closed, mirroring
   * setVideoExportPlan, so a route arriving after a close never resurrects a
   * shut wizard.
   */
  setVideoExportRoute: (route: RouteGeometry | null) => void;
  /**
   * setVideoExportStatus updates the preview/recording progress on the open
   * wizard. A no-op when the wizard is closed.
   */
  setVideoExportStatus: (status: VideoExportStatus) => void;
  /**
   * setVideoExportStep switches the active wizard step on the open wizard. A
   * no-op when the wizard is closed.
   */
  setVideoExportStep: (step: VideoExport["step"]) => void;
}

/**
 * videoExportInitialState seeds the wizard slot closed.
 */
export const videoExportInitialState: VideoExportSliceState = {
  videoExport: null,
};

/**
 * createVideoExportSlice builds the video-export wizard actions. Every narrow
 * setter no-ops while the wizard is closed so a late preview/route/status write
 * never resurrects a shut wizard.
 */
export const createVideoExportSlice: StateCreator<
  VideoExportSliceState & VideoExportSliceActions,
  [],
  [],
  VideoExportSliceActions
> = (set) => ({
  setVideoExport: (videoExport) => set({ videoExport }),

  setVideoExportActivityGlyph: (activityGlyphUrl) =>
    set((state) =>
      state.videoExport ? { videoExport: { ...state.videoExport, activityGlyphUrl } } : {},
    ),

  setVideoExportPlan: (plan) =>
    set((state) => (state.videoExport ? { videoExport: { ...state.videoExport, plan } } : {})),

  setVideoExportProfile: (profile) =>
    set((state) =>
      state.videoExport
        ? {
            videoExport: {
              ...state.videoExport,
              profile: {
                ...(state.videoExport.profile ?? DEFAULT_VIDEO_EXPORT_PROFILE),
                ...profile,
              },
            },
          }
        : {},
    ),

  setVideoExportProfileSource: (profileSource) =>
    set((state) =>
      state.videoExport ? { videoExport: { ...state.videoExport, profileSource } } : {},
    ),

  setVideoExportRoute: (route) =>
    set((state) => (state.videoExport ? { videoExport: { ...state.videoExport, route } } : {})),

  setVideoExportStatus: (status) =>
    set((state) => (state.videoExport ? { videoExport: { ...state.videoExport, status } } : {})),

  setVideoExportStep: (step) =>
    set((state) => (state.videoExport ? { videoExport: { ...state.videoExport, step } } : {})),
});
